import { useState, useEffect } from 'react';
import type { ColumnDef, CellContext } from "@tanstack/react-table";
import { NumberInput, ActionIcon, Tooltip, Group, Text, Select } from '@mantine/core';
import { IconTrash, IconEdit, IconCheck, IconX } from '@tabler/icons-react';
import type { IConfiguracionLogistica } from '../interfaces';

export interface LogisticaTableMeta {
  actualizarFila: (id: string, campo: keyof IConfiguracionLogistica | 'Cantidad', valor: string | number, indiceCantidad?: number) => void;
  eliminarFila: (id: string) => void;
  guardarFila?: (id: string) => void;
  filaEnEdicion?: string | null;
  iniciarEdicion?: (fila: any) => void;
  cancelarEdicion?: () => void;
  mostrarNotificacionObligatorio?: () => void;
  intentoGuardar?: boolean;
}

export interface LogisticaCatalogos {
  productos: { value: string; label: string }[];
  maquinas: { value: string; label: string }[];
  operadores: { value: string; label: string }[];
}

const EditableSelect = ({
  initialValue,
  rowId,
  campo,
  data,
  meta,
  opcional = false
}: {
  initialValue: string;
  rowId: string;
  campo: keyof IConfiguracionLogistica;
  data: { value: string; label: string }[];
  meta?: LogisticaTableMeta;
  /** Opcional: vacío = la fila aplica a cualquier valor (se muestra "-", como las demás celdas vacías). */
  opcional?: boolean;
}) => {
  const [value, setValue] = useState<string | null>(initialValue || null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(initialValue || null);
  }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    return <Text size="xs">{data.find(option => option.value === value)?.label || value || "-"}</Text>;
  }
  return (
    <Select
      size="xs"
      value={value}
      onChange={(val) => { setTouched(true); setValue(val); if (meta) meta.actualizarFila(rowId, campo, val || ""); }}
      data={data}
      placeholder={opcional ? (campo === 'Maquina' ? 'Máquina' : String(campo)) : `${campo} *`}
      required={!opcional}
      withAsterisk={!opcional}
      error={!opcional && (touched || meta?.intentoGuardar) && (!value || String(value).trim() === "")}
      allowDeselect={opcional}
      clearable={opcional}
      searchable
      nothingFoundMessage="Sin resultados"
      comboboxProps={{ withinPortal: true, zIndex: 9999 }}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
    />
  );
};

const EditableNumberInput = ({
  initialValue,
  rowId,
  campo,
  indiceCantidad,
  meta
}: {
  initialValue: number | string;
  rowId: string;
  campo: keyof IConfiguracionLogistica | 'Cantidad' | 'Cantidades';
  indiceCantidad?: number;
  meta?: LogisticaTableMeta;
}) => {
  const [value, setValue] = useState<number | string>(initialValue);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const isEditing = String(meta?.filaEnEdicion) === String(rowId);
  if (!isEditing) {
    return <Text size="xs">{value}</Text>;
  }
  return (
    <NumberInput
      size="xs"
      hideControls
      value={value !== 0 ? value : (value === 0 ? "0" : "")}
      onChange={(val) => { setTouched(true); setValue(val); if (meta) meta.actualizarFila(rowId, campo, val === "" ? "" : (Number(val) || 0), indiceCantidad); }}
      required
      withAsterisk
      placeholder="*"
      error={(touched || meta?.intentoGuardar) && (value === null || value === undefined || String(value) === "")}
      styles={{ input: { fontSize: "12px", padding: "4px 8px" } }}
    />
  );
};

export const obtenerColumnasLogistica = (cantidadColumnas: number = 12, catalogos: LogisticaCatalogos): ColumnDef<IConfiguracionLogistica>[] => [
  {
    id: "acciones",
    header: "Acciones",
    size: 90,
    cell: ({ row, table }) => {
      const meta = table.options.meta as LogisticaTableMeta | undefined;
      const isEditing = String(meta?.filaEnEdicion) === String(row.original.Id);
      const hayOtraFilaEnEdicion = meta?.filaEnEdicion !== null && meta?.filaEnEdicion !== undefined && String(meta?.filaEnEdicion) !== String(row.original.Id);
      
      if (isEditing) {
        return (
          <Group gap={4} justify="center">
            <Tooltip label="Guardar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => {
                const fila = row.original;
                // Máquina y operador son opcionales (vacío = aplica a cualquiera). Las 12 cantidades son
                // obligatorias y 0 es válido, igual que en la carga masiva.
                const cantidadVacia = (c: unknown) => c === null || c === undefined || String(c).trim() === "";
                const vacio = !fila.Producto || String(fila.Producto).trim() === "" ||
                              !fila.Paradas || String(fila.Paradas).trim() === "" ||
                              fila.Cantidades.length < cantidadColumnas || fila.Cantidades.some(cantidadVacia);
                
                if (vacio) {
                  meta?.mostrarNotificacionObligatorio?.();
                  return;
                }
                meta?.guardarFila?.(fila.Id);
              }}>
                <IconCheck size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Cancelar" withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
              <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" onClick={() => meta?.cancelarEdicion?.()}>
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        );
      }
      return (
        <Group gap={4} justify="center">
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Editar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
             <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => { meta?.iniciarEdicion?.(row.original); }}>
               <IconEdit size={18} />
             </ActionIcon>
          </Tooltip>
          <Tooltip label={hayOtraFilaEnEdicion ? "Termina la edición actual" : "Eliminar"} withinPortal zIndex={9999} style={{ fontSize: '10px', padding: '3px 5px' }}>
            <ActionIcon disabled={hayOtraFilaEnEdicion} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta?.eliminarFila(row.original.Id)}>
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      );
    }
  },
  {
    accessorKey: "Producto",
    header: "Producto",
    size: 130,
    cell: ({ row, getValue, table }) => (
      <EditableSelect 
        initialValue={(getValue() as string) || ""} 
        rowId={row.original.Id} 
        campo="Producto" 
        data={catalogos.productos}
        meta={table.options.meta as LogisticaTableMeta} 
      />
    )
  },
  {
    accessorKey: "Maquina",
    header: "Máquina",
    size: 130,
    cell: ({ row, getValue, table }) => (
      <EditableSelect 
        initialValue={(getValue() as string) || ""} 
        rowId={row.original.Id} 
        campo="Maquina" 
        data={catalogos.maquinas}
        meta={table.options.meta as LogisticaTableMeta} 
        // Opcional: vacío = la fila aplica a cualquier máquina del producto.
        opcional
      />
    )
  },
  {
    accessorKey: "Operador",
    header: "Operador",
    size: 100,
    cell: ({ row, getValue, table }) => (
      <EditableSelect 
        initialValue={(getValue() as string) || ""} 
        rowId={row.original.Id} 
        campo="Operador"
        data={catalogos.operadores}
        meta={table.options.meta as LogisticaTableMeta}
        // Opcional: vacío = la fila aplica a cualquier operador de puerta (como en el Excel).
        opcional
      />
    )
  },
  {
    accessorKey: "Paradas",
    header: "Paradas",
    size: 90,
    cell: ({ row, getValue, table }) => {
      const valor = getValue() as number | string | null | undefined;
      return (
        <EditableNumberInput 
          initialValue={valor !== undefined && valor !== null ? valor : ""} 
          rowId={row.original.Id} 
          campo="Paradas" 
          meta={table.options.meta as LogisticaTableMeta} 
        />
      );
    }
  },
  ...Array.from({ length: cantidadColumnas }).map((_, i) => ({
    id: `Cant_${i + 1}`,
    accessorFn: (row: IConfiguracionLogistica) => row.Cantidades ? row.Cantidades[i] : 0,
    header: `${i + 1}`,
    size: 80,
    cell: ({ row, table }: CellContext<IConfiguracionLogistica, unknown>) => (
      <EditableNumberInput 
        initialValue={row.original.Cantidades[i] !== undefined && row.original.Cantidades[i] !== null ? row.original.Cantidades[i] : ""} 
        rowId={row.original.Id} 
        campo="Cantidades" 
        indiceCantidad={i}
        meta={table.options.meta as LogisticaTableMeta} 
      />
    )
  })) as ColumnDef<IConfiguracionLogistica>[]
];
